import api, { type ApiResponse } from './axiosInstance';

export type BrainChatMessage = {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  name?: string;
  citations?: Array<{ sourceType: string; sourceId: string; label?: string }>;
};

export type BrainCollectedState = {
  projectId?: string;
  projectName?: string;
  lastPreviewSummary?: string;
  lastCommitSummary?: string;
  committed?: boolean;
  solarPlan?: Record<string, unknown>;
  capacity?: { value: number; unit: 'MW' | 'KW' };
  [key: string]: unknown;
};

export async function startBrainSession(payload?: {
  skillName?: string;
  contextRef?: { type?: 'project' | 'equipment' | 'task' | 'audit' | 'dpr'; id?: string };
}): Promise<{ sessionId: string; greeting: string; skillName: string; collectedState: BrainCollectedState }> {
  const res = await api.post<
    ApiResponse<{ sessionId: string; greeting: string; skillName: string; collectedState: BrainCollectedState }>
  >('/brain/sessions', payload || { skillName: 'architectProject' });
  if (!res.data.success) throw new Error(res.data.message || 'Failed to start brain session');
  return res.data.data;
}

export async function sendBrainMessage(
  sessionId: string,
  message: string
): Promise<{
  assistantMessage: string;
  collectedState: BrainCollectedState;
  toolTrace: Array<{ tool: string; ok: boolean; result?: unknown; error?: string }>;
  citations?: Array<{ sourceType: string; sourceId: string; label?: string }>;
  sessionCompleted: boolean;
}> {
  const res = await api.post<
    ApiResponse<{
      assistantMessage: string;
      collectedState: BrainCollectedState;
      toolTrace: Array<{ tool: string; ok: boolean; result?: unknown; error?: string }>;
      citations?: Array<{ sourceType: string; sourceId: string; label?: string }>;
      sessionCompleted: boolean;
    }>
  >(`/brain/sessions/${sessionId}/messages`, { message });
  if (!res.data.success) throw new Error(res.data.message || 'Failed to send brain message');
  return res.data.data;
}

export async function invokeSkill(
  skillName: string,
  input: Record<string, unknown>
): Promise<{
  assistantMessage: string;
  structured?: Record<string, unknown>;
  citations?: Array<{ sourceType: string; sourceId: string; label?: string }>;
  toolTrace: Array<{ tool: string; ok: boolean; result?: unknown; error?: string }>;
}> {
  const res = await api.post<
    ApiResponse<{
      assistantMessage: string;
      structured?: Record<string, unknown>;
      citations?: Array<{ sourceType: string; sourceId: string; label?: string }>;
      toolTrace: Array<{ tool: string; ok: boolean; result?: unknown; error?: string }>;
    }>
  >(`/brain/skills/${skillName}/invoke`, { input });
  if (!res.data.success) throw new Error(res.data.message || 'Failed to invoke skill');
  return res.data.data;
}

export async function submitBrainFeedback(payload: {
  suggestionId: string;
  feedback: 'accepted' | 'rejected' | 'edited';
  notes?: string;
}): Promise<void> {
  const res = await api.post<ApiResponse<{ ok: boolean }>>('/brain/feedback', payload);
  if (!res.data.success) throw new Error(res.data.message || 'Failed to submit feedback');
}

export async function resolveCitation(citationId: string): Promise<{ citationId: string; resolved: Record<string, unknown> }> {
  const res = await api.get<ApiResponse<{ citationId: string; resolved: Record<string, unknown> }>>(
    `/brain/citations/${citationId}`
  );
  if (!res.data.success) throw new Error(res.data.message || 'Failed to resolve citation');
  return res.data.data;
}
